import React, { useState, useRef, useEffect, useCallback } from "react";
import styled from "styled-components";
import {
  Button,
  Typography,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  Chip,
  Fade,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import HistoryIcon from "@mui/icons-material/History";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import TPC_URL from "../../../utils/TPC_URL";
import { useParams } from "react-router-dom";
import LotSettingModal from "./LotSettingModal";
import { CirclesWithBar } from "react-loader-spinner";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: var(--mui-palette-background-default);
  overflow: hidden;
  box-sizing: border-box;
`;

const FrameContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex: 1;
  overflow: hidden;
  background-color: var(--mui-palette-background-paper);
  box-shadow: var(--mui-shadows-2);
`;

const ToolbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid var(--mui-palette-divider);
`;

const ToolbarLeft = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const ToolbarRight = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const SplitContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  flex: 1;
  overflow: hidden;
`;

const UploadSection = styled.div`
  width: 70%;
  height: 100%;
  overflow-y: auto;
  padding: 24px;
  border-right: 1px solid var(--mui-palette-divider);
`;

const HistorySection = styled.div`
  width: 30%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const HistoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
`;

const LogListContainer = styled(List)`
  width: 100%;
  padding: 0;
  max-height: 100%;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(99, 96, 150, 0.7);
    border-radius: 5px;
  }
`;

const HistoryContent = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(99, 96, 150, 0.7);
    border-radius: 5px;
  }
`;

const UploadButton = styled(Button)`
  margin-top: 24px;
  height: 56px;
  font-size: 16px;
  font-weight: bold;
`;

const HiddenInput = styled.input`
  display: none;
`;

const FilePreviewWrapper = styled(Paper)`
  display: flex;
  align-items: center;
  padding: 16px;
  margin: 24px 0;
  width: 100%;
  max-width: 600px;
  border-radius: 12px;
  background-color: #f9f9f9;
  border-left: 5px solid #1976d2;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  }
`;

const FileIconWrapper = styled.div`
  background-color: #e3f2fd;
  border-radius: 50%;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
`;

const FileInfoWrapper = styled.div`
  flex: 1;
  overflow: hidden;
`;

const FileName = styled(Typography)`
  font-weight: 500;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileSize = styled(Typography)`
  font-size: 14px;
  color: #666;
`;

const DropZone = styled(Paper)`
  padding: 60px 40px;
  border: 3px dashed ${(props) => (props.isDragging ? "#1976d2" : "#ccc")};
  border-radius: 16px;
  background-color: ${(props) =>
    props.isDragging ? "rgba(25, 118, 210, 0.05)" : "rgba(245, 245, 245, 0.8)"};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 800px;
  min-height: 300px;
  transition: all 0.3s ease;
  cursor: pointer;
  &:hover {
    border-color: #1976d2;
    background-color: rgba(25, 118, 210, 0.05);
  }
`;

const ContentContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  gap: 1rem;
`;

const EmptyLogMessage = styled(Typography)`
  padding: 24px;
  text-align: center;
  color: #757575;
`;

const DimBox = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  backdrop-filter: blur(3px);
  background-color: rgba(0, 0, 0, 0.2);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const DimContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;

  p {
    color: var(--mui-palette-common-white);
    font-size: 1.2rem;
    font-weight: 500;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }
`;

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + " bytes";
  else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
  else return (bytes / 1048576).toFixed(1) + " MB";
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${month}-${day} ${hours}:${minutes}`;
};

function Main() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // 'success', 'error', 'warning', 'info'
  });
  const { group_code, project_code } = useParams();
  const [lotModalOpen, setLotModalOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedLogIds, setSelectedLogIds] = useState([]);
  const [processingMessage, setProcessingMessage] =
    useState("처리 중입니다...");
  const handleOpenLotModal = () => {
    setLotModalOpen(true);
  };
  const handleCloseLotModal = () => {
    setLotModalOpen(false);
  };
  const openSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };
  const closeSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };
  const resetForm = () => {
    setSelectedFile(null);
    setUploadStatus(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  const validateFile = (file) => {
    const excelTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel.sheet.macroEnabled.12",
      "application/vnd.ms-excel.sheet.binary.macroEnabled.12",
      "application/vnd.oasis.opendocument.spreadsheet",
      "text/csv",
    ];
    if (
      !excelTypes.includes(file.type) &&
      !(
        file.name.endsWith(".xls") ||
        file.name.endsWith(".xlsx") ||
        file.name.endsWith(".xlsm") ||
        file.name.endsWith(".xlsb") ||
        file.name.endsWith(".ods") ||
        file.name.endsWith(".csv")
      )
    ) {
      openSnackbar("엑셀 파일만 업로드 가능합니다.", "error");
      return false;
    }
    const fileName = file.name.split(".")[0];
    const fileNamePattern = /^[A-Za-z0-9]+_\d{4}(?:_\d+)?$/;
    if (!fileNamePattern.test(fileName)) {
      openSnackbar(
        "파일 이름은 코드_MMDD.xlsx 형식이어야 합니다. (예: AB1_0707.xlsx, CA01_0707_01.xlsx)",
        "error"
      );
      return false;
    }
    return true;
  };
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && validateFile(file)) {
      setSelectedFile(file);
      setUploadStatus(null);
      openSnackbar("파일이 선택되었습니다.", "info");
    } else {
      setSelectedFile(null);
    }
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && validateFile(file)) {
      setSelectedFile(file);
      setUploadStatus(null);
      openSnackbar("파일이 드롭되었습니다.", "info");
    }
  };
  const fetchLogs = useCallback(async () => {
    setInitialLoading(true);
    try {
      const response = await axios.get(
        TPC_URL +
          `/api/tpc/getImportLog?group_code=${group_code}&project_code=${project_code}`
      );
      if (response.data && Array.isArray(response.data)) {
        const sortedLogs = [...response.data].sort((a, b) => {
          const idA = Number(a.import_id);
          const idB = Number(b.import_id);
          return idB - idA;
        });
        setLogs(sortedLogs);
        setSelectedLogIds([]);
      } else {
        setLogs([]);
        setError("로그 데이터 형식이 유효하지 않습니다");
      }
    } catch (err) {
      console.error("로그 데이터 로딩 오류:", err);
      setError(`로그 데이터를 불러오는 중 오류가 발생했습니다: ${err.message}`);
      setLogs([]);
    } finally {
      setInitialLoading(false);
    }
  }, [group_code, project_code]);
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);
  const handleUpload = async () => {
    if (!selectedFile) return;
    const formData = new FormData();
    formData.append("excel", selectedFile);
    formData.append("group_code", group_code);
    formData.append("project_code", project_code);
    setIsUploading(true);
    setProcessingMessage("생산 계획 엑셀을 업로드하는 중입니다.");
    setUploadStatus(null);
    try {
      const response = await axios.post(
        TPC_URL + "/api/tpc/upload-excel",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setUploadStatus({
        success: true,
        message: "파일이 성공적으로 업로드되었습니다.",
        data: response.data,
      });
      openSnackbar("파일이 성공적으로 업로드되었습니다.", "success");
      setTimeout(() => {
        resetForm();
      }, 200);
      fetchLogs();
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      setUploadStatus({
        success: false,
        message: `업로드 실패: ${errorMessage}`,
      });
      openSnackbar(`업로드 실패: ${errorMessage}`, "error");
    } finally {
      setIsUploading(false);
    }
  };
  const handleZoneClick = () => {
    fileInputRef.current.click();
  };
  const handleRemoveFile = (e) => {
    e.stopPropagation();
    resetForm();
  };
  const handleDeleteLog = async (import_id) => {
    if (
      window.confirm(
        "해당 데이터를 삭제하시겠습니까? 이 작업은 취소할 수 없습니다."
      )
    ) {
      if (!import_id) return;
      setIsUploading(true);
      setProcessingMessage("데이터를 삭제하는 중입니다...");
      try {
        const response = await axios.post(TPC_URL + "/api/tpc/deleteRaw", {
          import_id,
          group_code,
          project_code,
        });
        if (response.data && response.data.success) {
          openSnackbar(`데이터가 성공적으로 삭제되었습니다.`, "success");
          fetchLogs();
        } else {
          openSnackbar(
            `데이터 삭제에 실패했습니다: ${
              response.data?.message || "알 수 없는 오류"
            }`,
            "error"
          );
        }
      } catch (error) {
        console.error("데이터 삭제 오류:", error);
        const errorMessage = error.response?.data?.message || error.message;
        openSnackbar(`데이터 삭제에 실패했습니다: ${errorMessage}`, "error");
      } finally {
        setIsUploading(false);
      }
    }
  };
  return (
    <Wrapper>
      <FrameContainer>
        <ToolbarContainer>
          <ToolbarLeft>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ fontSize: "22px !important", color: "#111" }}
            >
              생산 계획 엑셀 업로드
            </Typography>
          </ToolbarLeft>
          <ToolbarRight>
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={handleOpenLotModal}
            >
              LOT 설정
            </Button>
          </ToolbarRight>
        </ToolbarContainer>
        <SplitContainer>
          <UploadSection>
            <ContentContainer>
              <HiddenInput
                ref={fileInputRef}
                id="excel-file-input"
                type="file"
                accept=".xls,.xlsx,.xlsm,.xlsb,.ods,.csv"
                onChange={handleFileChange}
              />
              <Box
                width="100%"
                p={3}
                borderRadius={2}
                bgcolor="rgba(245, 245, 245, 0.8)"
              >
                <Typography variant="h6" color="textPrimary" gutterBottom>
                  파일 업로드 주의사항:
                </Typography>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  component="div"
                >
                  <ul>
                    <li>
                      모든 엑셀 파일 형식(.xls, .xlsx, .xlsm, .xlsb, .ods,
                      .csv)을 업로드 할 수 있습니다.
                    </li>
                    <li>
                      파일명은 <strong>코드_MMDD</strong> 또는{" "}
                      <strong>코드_MMDD_숫자</strong> 형식이어야 합니다.
                    </li>
                    <li>예시: CA01_0707.xlsx, AB1_0812_01.xlsx</li>
                  </ul>
                </Typography>
              </Box>
              <DropZone
                isDragging={isDragging}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleZoneClick}
                elevation={0}
              >
                <CloudUploadIcon
                  style={{ fontSize: 64, color: "#1976d2", marginBottom: 24 }}
                />
                <Typography
                  variant="h5"
                  align="center"
                  gutterBottom
                  fontWeight="medium"
                >
                  {selectedFile
                    ? "파일이 선택되었습니다"
                    : "파일을 끌어서 놓거나 클릭하여 선택하세요"}
                </Typography>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  align="center"
                >
                  지원 형식: .xls, .xlsx, .xlsm, .xlsb, .ods, .csv
                </Typography>
                {!selectedFile && (
                  <Chip
                    icon={<InsertDriveFileIcon />}
                    label="파일 선택"
                    color="primary"
                    variant="outlined"
                    sx={{ mt: 3 }}
                  />
                )}
              </DropZone>
              {selectedFile && (
                <Fade in={!!selectedFile}>
                  <FilePreviewWrapper elevation={3}>
                    <FileIconWrapper>
                      <InsertDriveFileIcon
                        style={{ fontSize: 32, color: "#1976d2" }}
                      />
                    </FileIconWrapper>
                    <FileInfoWrapper>
                      <FileName variant="h6">{selectedFile.name}</FileName>
                      <FileSize>
                        {formatFileSize(selectedFile.size)} •{" "}
                        {new Date().toLocaleDateString()}
                      </FileSize>
                    </FileInfoWrapper>
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="선택됨"
                      color="success"
                      variant="outlined"
                      sx={{ mr: 1 }}
                    />
                    <Button
                      color="error"
                      size="small"
                      variant="contained"
                      onClick={handleRemoveFile}
                      startIcon={<DeleteIcon />}
                    >
                      삭제
                    </Button>
                  </FilePreviewWrapper>
                </Fade>
              )}
              <UploadButton
                variant="contained"
                color="primary"
                onClick={handleUpload}
                disabled={!selectedFile || isUploading}
                fullWidth
                size="large"
                startIcon={isUploading ? null : <CloudUploadIcon />}
                sx={{ maxWidth: "600px" }}
              >
                {isUploading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "업로드"
                )}
              </UploadButton>
              {uploadStatus && (
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    mt: 3,
                    width: "100%",
                    maxWidth: "600px",
                    backgroundColor: uploadStatus.success
                      ? "#e8f5e9"
                      : "#ffebee",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    color={uploadStatus.success ? "success.dark" : "error.dark"}
                  >
                    {uploadStatus.message}
                  </Typography>
                </Paper>
              )}
            </ContentContainer>
          </UploadSection>
          <HistorySection>
            <HistoryHeader>
              <Typography variant="subtitle1" fontWeight="500">
                업로드 내역
              </Typography>
              <Button
                startIcon={<HistoryIcon />}
                onClick={fetchLogs}
                variant="outlined"
              >
                새로고침
              </Button>
            </HistoryHeader>
            <HistoryContent>
              <LogListContainer>
                {initialLoading ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress size={24} />
                  </Box>
                ) : error ? (
                  <EmptyLogMessage color="error">{error}</EmptyLogMessage>
                ) : logs.length === 0 ? (
                  <EmptyLogMessage>업로드 내역이 없습니다.</EmptyLogMessage>
                ) : (
                  logs.map((log, index) => (
                    <div key={log.import_id || index}>
                      <ListItem>
                        <ListItemText
                          primary={`파일명 : ${log.file_name}`}
                          secondary={
                            log.import_date ? (
                              <div>
                                {`업로드 일시 : ${formatDate(
                                  log.import_date
                                )} / 데이터 개수 : `}
                                <span style={{ fontWeight: "bold" }}>
                                  {log.record_count}개
                                </span>
                              </div>
                            ) : (
                              "날짜 정보 없음"
                            )
                          }
                          primaryTypographyProps={{
                            style: { fontWeight: 500 },
                          }}
                        />
                        <ListItemSecondaryAction>
                          <Tooltip title="데이터 삭제">
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              color="error"
                              onClick={() => handleDeleteLog(log.import_id)}
                              disabled={isUploading}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < logs.length && (
                        <Divider
                          sx={{
                            height: "3px",
                            backgroundColor: "#e0e0e0",
                          }}
                        />
                      )}
                    </div>
                  ))
                )}
              </LogListContainer>
            </HistoryContent>
          </HistorySection>
        </SplitContainer>
      </FrameContainer>
      <LotSettingModal
        open={lotModalOpen}
        handleClose={handleCloseLotModal}
        group_code={group_code}
        project_code={project_code}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      {isUploading && (
        <DimBox>
          <DimContent>
            <CirclesWithBar
              visible={true}
              height="100"
              width="100"
              color="#ffffff"
              outerCircleColor="#ffffff"
              innerCircleColor="#ffffff"
              barColor="#4fa94d"
              ariaLabel="circles-with-bar-loading"
            />
            <Typography variant="h6" color="white">
              {processingMessage}
            </Typography>
          </DimContent>
        </DimBox>
      )}
    </Wrapper>
  );
}

export default Main;
